import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/api';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const hasVerified = React.useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasVerified.current) {
      return;
    }

    const token = searchParams.get('token');
    
    if (!token) {
      setVerificationStatus('error');
      setErrorMessage('Invalid verification link. No token provided.');
      return;
    }

    // Decode the token in case it was URL-encoded
    const decodedToken = decodeURIComponent(token);
    console.log('Original token:', token);
    console.log('Decoded token:', decodedToken);
    
    hasVerified.current = true;
    verifyEmail(decodedToken);
  }, [searchParams]);

  const verifyEmail = async (token: string) => {
    try {
      const response = await apiService.verifyEmail(token);
      
      if (response.success) {
        setVerificationStatus('success');
        toast.success('Email verified successfully! You can now log in.');
        
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setVerificationStatus('error');
        setErrorMessage(response.message || 'Email verification failed.');
      }
    } catch (error: any) {
      setVerificationStatus('error');
      setErrorMessage(error?.response?.data?.message || 'An error occurred during email verification.');
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            {verificationStatus === 'verifying' && (
              <>
                <Loader2 className="mx-auto h-12 w-12 text-blue-600 animate-spin" />
                <h2 className="mt-6 text-2xl font-bold text-gray-900 dark:text-white">
                  Verifying Your Email
                </h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Please wait while we verify your email address...
                </p>
              </>
            )}

            {verificationStatus === 'success' && (
              <>
                <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
                <h2 className="mt-6 text-2xl font-bold text-gray-900 dark:text-white">
                  Email Verified Successfully!
                </h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Your email address has been verified. You can now log in to your account.
                </p>
                <div className="mt-6">
                  <button
                    onClick={handleGoToLogin}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Go to Login
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  You will be automatically redirected in a few seconds...
                </p>
              </>
            )}

            {verificationStatus === 'error' && (
              <>
                <XCircle className="mx-auto h-12 w-12 text-red-600" />
                <h2 className="mt-6 text-2xl font-bold text-gray-900 dark:text-white">
                  Verification Failed
                </h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {errorMessage}
                </p>
                <div className="mt-6">
                  <button
                    onClick={handleGoToLogin}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Go to Login
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
